import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Shield, ChevronDown, ChevronUp, ChevronLeft, Terminal, FileCode, MessageSquare, X, Maximize2, Minimize2, Sparkles, PanelBottomClose, PanelBottomOpen, GripHorizontal, RotateCcw, Search, Eye, CheckCircle2, Info } from 'lucide-react';
import type { NetworkSecurityFinding, NetworkSecurityResults } from './hooks/useNetworkSecurity';
import {
  resolveToSecurityGroups,
  getSearchResolutionDescription,
  type DiscoveryData,
} from './hooks/networkSecuritySearchUtils';

// Unified security resource type for display
export interface SecurityResource {
  id: string;
  visualNodeId: string;
  name: string;
  type: 'security_group' | 'nacl' | 'security_list' | 'network_security_group';
  provider: 'aws' | 'oci';
  vpcId?: string;
  vpcName?: string;
  rulesCount?: number;
  rawData?: unknown; // For opening the full rules modal
}

interface Props {
  results: NetworkSecurityResults | null;
  loading: boolean;
  selectedFinding: NetworkSecurityFinding | null;
  highlightedSgId: string | null;
  sgsWithEnis: Set<string>;
  allSecurityResources?: SecurityResource[]; // ALL security resources (even without findings)
  discoveryData?: DiscoveryData | null; // Discovery data for advanced search resolution
  onSelectFinding: (f: NetworkSecurityFinding | null) => void;
  onToggleHighlight: (sgId: string) => void;
  onAskAI: (f: NetworkSecurityFinding) => void;
  onClose: () => void;
  onViewAllRules?: (resource: SecurityResource) => void; // Open full rules modal
}

const SEV = {
  critical: {
    bg: 'bg-red-500',
    text: 'text-red-700',
    light: 'bg-red-50',
    border: 'border-red-200',
    ring: 'ring-red-500',
    semanticSelected: 'border-semantic-border-danger-primary bg-semantic-bg-danger-secondary',
    semanticPill: 'bg-semantic-bg-danger-secondary text-semantic-text-danger',
  },
  high: {
    bg: 'bg-orange-500',
    text: 'text-orange-700',
    light: 'bg-orange-50',
    border: 'border-orange-200',
    ring: 'ring-orange-500',
    semanticSelected: 'border-semantic-border-warning-primary bg-semantic-bg-warning-secondary',
    semanticPill: 'bg-semantic-bg-warning-secondary text-semantic-text-warning',
  },
  medium: {
    bg: 'bg-yellow-500',
    text: 'text-yellow-700',
    light: 'bg-yellow-50',
    border: 'border-yellow-200',
    ring: 'ring-yellow-500',
    semanticSelected: 'border-semantic-border-warning-primary bg-semantic-bg-warning-secondary',
    semanticPill: 'bg-semantic-bg-warning-secondary text-semantic-text-warning',
  },
  low: {
    bg: 'bg-green-500',
    text: 'text-green-700',
    light: 'bg-green-50',
    border: 'border-green-200',
    ring: 'ring-green-500',
    semanticSelected: 'border-semantic-border-success-primary bg-semantic-bg-success-secondary',
    semanticPill: 'bg-semantic-bg-success-secondary text-semantic-text-success',
  },
};

const SEV_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

const SEV_ICON: Record<string, string> = {
  critical: '[!]',
  high: '[H]',
  medium: '[M]',
  low: '[L]',
};

// Resource type labels
const RESOURCE_TYPE_LABEL: Record<string, string> = {
  'security_group': 'SG',
  'nacl': 'NACL',
  'network_security_group': 'NSG',
  'security_list': 'SL',
};

interface ResourceGroup {
  resourceId: string;
  resourceName: string;
  resourceType: string;
  visualNodeId: string;
  findings: NetworkSecurityFinding[];
  highestSeverity: 'critical' | 'high' | 'medium' | 'low';
  summary: { critical: number; high: number; medium: number; low: number };
}

export const NetworkSecurityPanel: React.FC<Props> = ({ 
  results, 
  loading,
  selectedFinding, 
  highlightedSgId,
  sgsWithEnis,
  allSecurityResources = [],
  discoveryData,
  onSelectFinding, 
  onToggleHighlight, 
  onAskAI,
  onClose,
  onViewAllRules
}) => {
  const [minimized, setMinimized] = useState(false);
  const [maximized, setMaximized] = useState(false);
  const [filter, setFilter] = useState<string | null>(null);
  const [showRem, setShowRem] = useState<string | null>(null);
  const [selectedResource, setSelectedResource] = useState<ResourceGroup | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllResources, setShowAllResources] = useState(false); // Toggle to show ALL resources
  const [searchResolutionMessage, setSearchResolutionMessage] = useState<string | null>(null);
  const [searchFoundResource, setSearchFoundResource] = useState(false); // True if search resolved to a known resource

  // Count resources with findings (independent of showAllResources toggle)
  const resourcesWithIssuesCount = useMemo(() => {
    if (!results) return 0;
    const unique = new Set<string>();
    for (const f of results.findings || []) unique.add(String(f.resourceId));
    return unique.size;
  }, [results]);
  
  // Draggable position state
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  
  // Reset position when maximized
  useEffect(() => {
    if (maximized) {
      setPosition(null);
    }
  }, [maximized]);
  
  // Handle drag start
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (maximized) return;
    e.preventDefault();
    e.stopPropagation();
    
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    // Initialize position if not set (first drag)
    const currentX = position?.x ?? 0;
    const currentY = position?.y ?? 0;
    
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: currentX,
      startPosY: currentY,
    };
    setIsDragging(true);
  }, [position, maximized]);
  
  // Handle drag move
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      
      const deltaX = e.clientX - dragRef.current.startX;
      const deltaY = e.clientY - dragRef.current.startY;
      
      setPosition({
        x: dragRef.current.startPosX + deltaX,
        y: dragRef.current.startPosY + deltaY,
      });
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      dragRef.current = null;
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);
  
  // Calculate panel style based on position
  const getPanelStyle = useCallback((): React.CSSProperties => {
    if (maximized) {
      return {};
    }
    if (position) {
      return {
        transform: `translate(calc(-50% + ${position.x}px), ${position.y}px)`,
      };
    }
    return {};
  }, [position, maximized]);

  // Group findings by resource AND include resources without findings
  const resourceGroups = useMemo(() => {
    const groups: Record<string, ResourceGroup & { rawData?: unknown; hasFindings: boolean }> = {};
    
    // Build a lookup map for rawData from allSecurityResources
    const rawDataMap: Record<string, unknown> = {};
    allSecurityResources.forEach(res => {
      rawDataMap[res.id] = res.rawData;
    });
    
    // First, add all resources from findings
    if (results) {
      results.findings.forEach(f => {
        const key = f.resourceId;
        if (!groups[key]) {
          groups[key] = {
            resourceId: f.resourceId,
            resourceName: f.resourceName,
            resourceType: f.resourceType,
            visualNodeId: f.visualNodeId,
            findings: [],
            highestSeverity: f.severity as 'critical' | 'high' | 'medium' | 'low',
            summary: { critical: 0, high: 0, medium: 0, low: 0 },
            rawData: rawDataMap[f.resourceId], // Always include rawData from lookup
            hasFindings: true,
          };
        }
        groups[key].findings.push(f);
        groups[key].summary[f.severity as keyof typeof groups[typeof key]['summary']]++;
        
        // Update highest severity
        if (SEV_ORDER[f.severity as keyof typeof SEV_ORDER] < SEV_ORDER[groups[key].highestSeverity]) {
          groups[key].highestSeverity = f.severity as 'critical' | 'high' | 'medium' | 'low';
        }
      });
    }
    
    // Then, add resources without findings (if showAllResources is enabled)
    if (showAllResources && allSecurityResources.length > 0) {
      allSecurityResources.forEach(res => {
        if (!groups[res.id]) {
          groups[res.id] = {
            resourceId: res.id,
            resourceName: res.name,
            resourceType: res.type,
            visualNodeId: res.visualNodeId,
            findings: [],
            highestSeverity: 'low',
            summary: { critical: 0, high: 0, medium: 0, low: 0 },
            rawData: res.rawData,
            hasFindings: false,
          };
        } else {
          // Add rawData to existing group (in case it wasn't in findings)
          groups[res.id].rawData = res.rawData;
        }
      });
    }
    
    // Sort: resources WITH findings first (by severity), then resources WITHOUT findings
    return Object.values(groups).sort((a, b) => {
      // Resources with findings come first
      if (a.hasFindings && !b.hasFindings) return -1;
      if (!a.hasFindings && b.hasFindings) return 1;
      
      // Both have findings: sort by severity then count
      if (a.hasFindings && b.hasFindings) {
        const sevDiff = SEV_ORDER[a.highestSeverity] - SEV_ORDER[b.highestSeverity];
        if (sevDiff !== 0) return sevDiff;
        return b.findings.length - a.findings.length;
      }
      
      // Both without findings: sort alphabetically
      return a.resourceName.localeCompare(b.resourceName);
    });
  }, [results, allSecurityResources, showAllResources]);

  // Filter resource groups by severity filter AND search query
  // Uses advanced search resolution when discoveryData is available
  const filteredGroups = useMemo(() => {
    let filtered = resourceGroups;
    let resolutionMsg: string | null = null;
    let foundResource = false;
    
    // Filter by severity
    if (filter) {
      filtered = filtered.filter(g => g.summary[filter as keyof typeof g.summary] > 0);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      const portQuery = parseInt(query, 10);
      const isPortSearch = !isNaN(portQuery) && portQuery > 0 && portQuery <= 65535;
      
      // Try advanced search resolution if discoveryData is available
      if (discoveryData) {
        const resolutionResult = resolveToSecurityGroups(searchQuery.trim(), discoveryData);
        
        // Check if we found any network resources (subnets, VNICs, etc.)
        const foundNetworkResource = resolutionResult.resolvedFrom.matchedResources.length > 0;
        
        if (resolutionResult.sgIds.length > 0) {
          // Filter by resolved SG IDs
          filtered = filtered.filter(g => resolutionResult.sgIds.includes(g.resourceId));
          foundResource = true;
          
          // Generate resolution description message
          resolutionMsg = getSearchResolutionDescription(resolutionResult, searchQuery.trim());
        } else if (foundNetworkResource) {
          // Found the network resource but no security resources with findings
          filtered = [];
          foundResource = true;
        } else {
          // No resolution found - check if it's a recognizable resource type pattern
          const looksLikeResourceId = 
            // AWS patterns
            /^(subnet-|eni-|acl-|vpc-|sg-)/.test(searchQuery.trim()) ||
            // OCI patterns (OCIDs)
            /ocid1\.(subnet|vnic|networksecuritygroup|securitylist|vcn)\./.test(searchQuery.trim()) ||
            // IP address
            /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/.test(searchQuery.trim());
          
          if (looksLikeResourceId) {
            // Resource ID pattern but not found in discovery data
            filtered = [];
            foundResource = true; // Treat as "resource-type search" for message purposes
          } else {
            // Fall back to legacy search (name/port/protocol)
            filtered = filtered.filter(g => {
              // Match by resource name
              if (g.resourceName.toLowerCase().includes(query)) return true;
              if (g.resourceId.toLowerCase().includes(query)) return true;
              
              // Match by port in findings
              if (isPortSearch) {
                return g.findings.some(f => {
                  const portRange =
                    f.violatingRule?.portRange ||
                    (f as unknown as { portRange?: string }).portRange ||
                    '';
                  if (portRange === 'ALL' || portRange === '-1') return true;
                  if (portRange.includes('-')) {
                    const [start, end] = portRange.split('-').map(Number);
                    return portQuery >= start && portQuery <= end;
                  }
                  return parseInt(portRange, 10) === portQuery;
                });
              }
              
              // Match by protocol or title in findings
              return g.findings.some(f => 
                f.title?.toLowerCase().includes(query) ||
                f.violatingRule?.protocol?.toLowerCase().includes(query)
              );
            });
          }
        }
      } else {
        // No discoveryData - use legacy search only
        filtered = filtered.filter(g => {
          // Match by resource name
          if (g.resourceName.toLowerCase().includes(query)) return true;
          if (g.resourceId.toLowerCase().includes(query)) return true;
          
          // Match by port in findings
          if (isPortSearch) {
            return g.findings.some(f => {
              const portRange =
                f.violatingRule?.portRange ||
                (f as unknown as { portRange?: string }).portRange ||
                '';
              if (portRange === 'ALL' || portRange === '-1') return true;
              if (portRange.includes('-')) {
                const [start, end] = portRange.split('-').map(Number);
                return portQuery >= start && portQuery <= end;
              }
              return parseInt(portRange, 10) === portQuery;
            });
          }
          
          // Match by protocol or title in findings
          return g.findings.some(f => 
            f.title?.toLowerCase().includes(query) ||
            f.violatingRule?.protocol?.toLowerCase().includes(query)
          );
        });
      }
    }
    
    // Update state via setTimeout to avoid state update during render
    setTimeout(() => {
      setSearchResolutionMessage(resolutionMsg);
      setSearchFoundResource(foundResource);
    }, 0);
    
    return filtered;
  }, [resourceGroups, filter, searchQuery, discoveryData]);

  // Filter findings within selected resource by severity
  const filteredFindings = useMemo(() => {
    if (!selectedResource) return [];
    if (!filter) return selectedResource.findings;
    return selectedResource.findings.filter(f => f.severity === filter);
  }, [selectedResource, filter]);

  // Handle back navigation
  const handleBack = () => {
    setSelectedResource(null);
    onSelectFinding(null);
    setShowRem(null);
  };

  if (loading) {
    return (
      <div className="absolute bottom-4 left-4 z-40">
        <div className="bg-semantic-bg-secondary backdrop-blur-sm rounded-2xl shadow-lg border border-semantic-border-primary px-4 py-3">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-semantic-text-brand animate-pulse" />
            <span className="text-semantic-text-secondary text-sm">Loading security findings...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="absolute bottom-4 left-4 z-40">
        <div className="bg-semantic-bg-secondary backdrop-blur-sm rounded-2xl shadow-lg border border-semantic-border-primary px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-semantic-text-secondary" />
              <span className="text-semantic-text-secondary text-sm">No security findings available.</span>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-semantic-bg-tertiary rounded-lg">
              <X className="w-4 h-4 text-semantic-text-secondary" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const panelMaxHeight = maximized ? 'calc(100vh - 120px)' : '380px';
  const listMaxHeight = maximized ? 'calc(100vh - 200px)' : '300px';

  // Minimized view
  if (minimized) {
    const minimizedStyle: React.CSSProperties = position 
      ? { transform: `translate(calc(-50% + ${position.x}px), ${position.y}px)` }
      : {};
    
    return (
      <div 
        ref={panelRef}
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] bg-semantic-bg-secondary rounded-full shadow-2xl border-2 border-semantic-border-brand-primary hover:shadow-xl hover:border-semantic-border-brand-secondary transition-shadow select-none ${isDragging ? 'cursor-grabbing' : ''}`}
        style={minimizedStyle}
        onMouseDown={e => e.stopPropagation()}
        onPointerDown={e => e.stopPropagation()}
      >
        <div className="flex items-center">
          {/* Drag handle - double click to reset position */}
          <div 
            className="px-2 py-2.5 cursor-grab hover:bg-violet-50 rounded-l-full transition-colors flex items-center"
            onMouseDown={handleDragStart}
            onDoubleClick={() => setPosition(null)}
            title="Drag to move, double-click to reset"
          >
            <GripHorizontal className="w-4 h-4 text-violet-300" />
          </div>
          <div 
            className="flex items-center gap-3 pr-3 py-2.5 cursor-pointer"
            onClick={() => setMinimized(false)}
          >
            <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-violet-600" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-semantic-text-primary">Security</span>
              <span className="text-sm font-bold text-violet-600">{results.score}/100</span>
            </div>
            <div className="flex items-center gap-1.5">
              {results.summary.critical > 0 && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-500 text-white">{results.summary.critical}</span>
              )}
              {results.summary.high > 0 && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-orange-500 text-white">{results.summary.high}</span>
              )}
              {results.summary.medium > 0 && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-yellow-500 text-white">{results.summary.medium}</span>
              )}
              {results.summary.low > 0 && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-green-500 text-white">{results.summary.low}</span>
              )}
            </div>
            <PanelBottomOpen className="w-5 h-5 text-violet-400" />
          </div>
          {position && (
            <button 
              onClick={e => { e.stopPropagation(); setPosition(null); }} 
              className="p-1.5 hover:bg-violet-100 rounded-full mr-1"
              title="Reset position"
            >
              <RotateCcw className="w-4 h-4 text-violet-400" />
            </button>
          )}
          <button 
            onClick={e => { e.stopPropagation(); onClose(); }} 
            className="p-1.5 hover:bg-red-100 rounded-full mr-2"
            title="Close security panel"
          >
            <X className="w-4 h-4 text-semantic-text-secondary hover:text-semantic-text-danger" />
          </button>
        </div>
      </div>
    );
  }

  // Main panel
  return (
    <div 
      ref={panelRef}
      className={`absolute left-1/2 z-40 w-[750px] max-w-[calc(100%-2rem)] transition-all ${isDragging ? 'transition-none' : 'duration-300'} ${maximized ? 'bottom-2 top-16 -translate-x-1/2' : 'bottom-4 -translate-x-1/2'}`}
      style={getPanelStyle()}
      onMouseDown={e => e.stopPropagation()}
      onPointerDown={e => e.stopPropagation()}
    >
      <div className="bg-semantic-bg-secondary backdrop-blur-sm rounded-2xl shadow-xl border border-semantic-border-primary overflow-hidden flex flex-col h-full" style={{ maxHeight: panelMaxHeight }}>
        {/* Header - Draggable, double-click to reset position */}
        <div 
          className={`px-4 py-2 border-b border-semantic-border-secondary bg-semantic-bg-tertiary flex-shrink-0 ${!maximized ? 'cursor-grab' : ''} ${isDragging ? 'cursor-grabbing' : ''}`}
          onMouseDown={handleDragStart}
          onDoubleClick={() => setPosition(null)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Drag indicator */}
              {!maximized && (
                <div className="flex items-center text-violet-300 mr-1" title="Drag to move">
                  <GripHorizontal className="w-4 h-4" />
                </div>
              )}
              {/* Back button when in drill-down */}
              {selectedResource && (
                <button 
                  onClick={handleBack}
                  onMouseDown={e => e.stopPropagation()}
                  className="p-1.5 hover:bg-semantic-bg-tertiary rounded-lg mr-1 transition-colors"
                  title="Back to resources"
                >
                  <ChevronLeft className="w-5 h-5 text-semantic-text-secondary" />
                </button>
              )}
              <div className="w-8 h-8 bg-semantic-bg-brand-secondary rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-semantic-text-brand" />
              </div>
              <div>
                {selectedResource ? (
                  <>
                    <h3 className="font-semibold text-semantic-text-primary flex items-center gap-2">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${SEV[selectedResource.highestSeverity].bg} text-white`}>
                        {RESOURCE_TYPE_LABEL[selectedResource.resourceType] || selectedResource.resourceType.toUpperCase()}
                      </span>
                      <span className="font-mono text-sm">{selectedResource.resourceName}</span>
                    </h3>
                    <p className="text-xs text-semantic-text-secondary">
                      {selectedResource.findings.length} finding{selectedResource.findings.length !== 1 ? 's' : ''}
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="font-semibold text-semantic-text-primary">Network Security</h3>
                    <p className="text-xs text-semantic-text-secondary">
                      Score: <span className="font-bold text-semantic-text-brand">{results.score}/100</span> - {resourcesWithIssuesCount} resource{resourcesWithIssuesCount !== 1 ? 's' : ''} with issues
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4" onMouseDown={e => e.stopPropagation()}>
              {/* Severity filters */}
              <div className="flex items-center gap-2">
                {(['critical', 'high', 'medium', 'low'] as const).map(sev => (
                  <button
                    key={sev}
                    onClick={() => setFilter(filter === sev ? null : sev)}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all ${
                      filter === sev 
                        ? `${SEV[sev].bg} text-white` 
                        : `${SEV[sev].semanticPill} hover:opacity-80`
                    }`}
                  >
                    {SEV_ICON[sev]} {results.summary[sev]}
                  </button>
                ))}
              </div>
              <button 
                onClick={() => setMinimized(true)} 
                className="p-1.5 hover:bg-semantic-bg-tertiary rounded-lg"
                title="Minimize to bar"
              >
                <PanelBottomClose className="w-4 h-4 text-semantic-text-secondary" />
              </button>
              {position && (
                <button 
                  onClick={() => setPosition(null)} 
                  className="p-1.5 hover:bg-semantic-bg-tertiary rounded-lg"
                  title="Reset position"
                >
                  <RotateCcw className="w-4 h-4 text-semantic-text-secondary" />
                </button>
              )}
              <button 
                onClick={() => { setMaximized(!maximized); setPosition(null); }} 
                className="p-1.5 hover:bg-semantic-bg-tertiary rounded-lg"
                title={maximized ? 'Reduce size' : 'Expand panel'}
              >
                {maximized ? <Minimize2 className="w-4 h-4 text-semantic-text-secondary" /> : <Maximize2 className="w-4 h-4 text-semantic-text-secondary" />}
              </button>
              <button onClick={onClose} className="p-1.5 hover:bg-semantic-bg-tertiary rounded-lg" title="Close">
                <X className="w-4 h-4 text-semantic-text-secondary" />
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar and Filters - Only on resource list view */}
        {!selectedResource && (
          <div className="px-3 py-2 border-b border-semantic-border-secondary bg-semantic-bg-tertiary/50 flex-shrink-0" onMouseDown={e => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-semantic-text-secondary" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by subnet, IP, security group, ENI, NACL..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-semantic-border-primary rounded-lg bg-semantic-bg-secondary text-semantic-text-primary placeholder:text-semantic-text-secondary focus:outline-none focus:ring-2 focus:ring-semantic-outline-primary focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => { setSearchQuery(''); setSearchResolutionMessage(null); setSearchFoundResource(false); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-semantic-bg-tertiary rounded"
                  >
                    <X className="w-3 h-3 text-semantic-text-secondary" />
                  </button>
                )}
              </div>
              
              {/* Toggle: Show all resources */}
              {allSecurityResources.length > 0 && (
                <div
                  className="flex items-center rounded-lg border border-semantic-border-primary bg-semantic-bg-tertiary p-0.5 whitespace-nowrap"
                  title="Toggle between showing only resources with findings vs all resources"
                >
                  <button
                    onClick={() => setShowAllResources(false)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-all ${
                      !showAllResources
                        ? 'bg-semantic-bg-brand-secondary text-semantic-text-brand'
                        : 'text-semantic-text-secondary hover:bg-semantic-bg-tertiary'
                    }`}
                  >
                    <Shield className="w-3.5 h-3.5" />
                    Issues only
                  </button>
                  <button
                    onClick={() => setShowAllResources(true)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-all ${
                      showAllResources
                        ? 'bg-semantic-bg-brand-secondary text-semantic-text-brand'
                        : 'text-semantic-text-secondary hover:bg-semantic-bg-tertiary'
                    }`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    All resources
                  </button>
                </div>
              )}
            </div>
            
            {/* Search resolution message */}
            {searchResolutionMessage && searchQuery && (
              <div className="mt-2 flex items-center gap-2 px-2 py-1.5 bg-semantic-bg-brand-secondary border border-semantic-border-brand-primary rounded-lg">
                <Info className="w-3.5 h-3.5 text-semantic-text-brand shrink-0" />
                <span className="text-xs text-semantic-text-brand">{searchResolutionMessage}</span>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto p-2 flex-1" style={{ maxHeight: listMaxHeight }} onMouseDown={e => e.stopPropagation()}>
          {/* LEVEL 1: Resource List */}
          {!selectedResource && (
            <div className="space-y-1.5">
              {filteredGroups.map((group) => {
                const hasFindings = group.findings.length > 0;
                const c = hasFindings ? SEV[group.highestSeverity] : { bg: 'bg-emerald-500', text: 'text-emerald-700', light: 'bg-emerald-50', border: 'border-emerald-200' };
                const hasEnis = sgsWithEnis.has(group.visualNodeId);
                const isHighlightable = hasEnis;
                const isActive = highlightedSgId === group.visualNodeId;
                
                // Build security resource object for view all rules
                const securityResource: SecurityResource = {
                  id: group.resourceId,
                  visualNodeId: group.visualNodeId,
                  name: group.resourceName,
                  type: group.resourceType as SecurityResource['type'],
                  provider: group.resourceType === 'security_group' || group.resourceType === 'nacl' ? 'aws' : 'oci',
                  rawData: (group as unknown as { rawData?: unknown }).rawData,
                };
                
                return (
                  <div 
                    key={group.resourceId}
                    className={`rounded-lg border transition-all hover:shadow-sm ${
                      isActive 
                        ? 'border-semantic-border-warning-secondary bg-semantic-bg-warning-secondary shadow-sm' 
                        : hasFindings
                          ? 'border-semantic-border-primary bg-semantic-bg-secondary hover:border-semantic-border-secondary'
                          : 'border-semantic-border-success-primary bg-semantic-bg-success-secondary/50 hover:border-semantic-border-success-secondary'
                    }`}
                  >
                    <div className="px-3 py-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <div 
                          className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                          onClick={() => hasFindings ? setSelectedResource(group) : null}
                        >
                          {/* Resource type badge */}
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${c.bg} text-white shrink-0`}>
                            {RESOURCE_TYPE_LABEL[group.resourceType] || group.resourceType.slice(0, 4).toUpperCase()}
                          </span>
                          {/* Resource name */}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-semantic-text-primary text-sm font-mono truncate" title={group.resourceName}>
                              {group.resourceName}
                            </div>
                          </div>
                        </div>
                        {/* Right side: severity summary + actions */}
                        <div className="flex items-center gap-2 shrink-0">
                          {hasFindings ? (
                            <>
                              {/* Severity counts */}
                              <div className="flex items-center gap-1">
                                {group.summary.critical > 0 && (
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-semantic-bg-danger-secondary text-semantic-text-danger">{group.summary.critical}</span>
                                )}
                                {group.summary.high > 0 && (
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-semantic-bg-warning-secondary text-semantic-text-warning">{group.summary.high}</span>
                                )}
                                {group.summary.medium > 0 && (
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-semantic-bg-warning-secondary text-semantic-text-warning">{group.summary.medium}</span>
                                )}
                                {group.summary.low > 0 && (
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-semantic-bg-success-secondary text-semantic-text-success">{group.summary.low}</span>
                                )}
                              </div>
                              {/* Total findings */}
                              <span className="text-xs text-semantic-text-secondary">
                                {group.findings.length} issue{group.findings.length !== 1 ? 's' : ''}
                              </span>
                            </>
                          ) : (
                            <span className="text-xs text-semantic-text-success font-medium flex items-center gap-1">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              No issues
                            </span>
                          )}
                          
                          {/* View All Rules button */}
                          {onViewAllRules && (
                            <button 
                              onClick={e => { 
                                e.stopPropagation(); 
                                onViewAllRules(securityResource);
                              }} 
                              className="p-1.5 rounded transition-all hover:bg-semantic-bg-brand-secondary text-semantic-text-secondary hover:text-semantic-text-brand"
                              title="View all rules"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          
                          {/* Highlight button */}
                          <button 
                            onClick={e => { 
                              e.stopPropagation(); 
                              if (isHighlightable) {
                                onToggleHighlight(group.visualNodeId);
                              }
                            }} 
                            disabled={!isHighlightable}
                            className={`p-1.5 rounded transition-all ${
                              !isHighlightable
                                ? 'text-semantic-text-secondary opacity-50 cursor-not-allowed'
                                : isActive 
                                  ? 'bg-semantic-bg-warning-secondary text-semantic-text-warning ring-2 ring-semantic-border-warning-secondary' 
                                  : 'hover:bg-semantic-bg-warning-secondary text-semantic-text-secondary hover:text-semantic-text-warning'
                            }`}
                            title={!isHighlightable ? 'No ENIs/VNICs use this resource' : isActive ? 'Turn off highlight' : 'Highlight affected ENIs/VNICs'}
                          >
                            <Sparkles className="w-4 h-4" />
                          </button>
                          
                          {/* Chevron - only show if has findings */}
                          {hasFindings && (
                            <button
                              onClick={() => setSelectedResource(group)}
                              className="p-1 hover:bg-semantic-bg-tertiary rounded"
                            >
                              <ChevronDown className="w-4 h-4 text-semantic-text-secondary" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {filteredGroups.length === 0 && (
                <div className="text-center py-8 text-semantic-text-secondary">
                  {searchQuery 
                    ? (searchFoundResource
                        ? `No security findings related to "${searchQuery}"`
                        : `No security findings matching "${searchQuery}"`)
                    : filter 
                      ? `No resources with ${filter} severity findings` 
                      : 'No security findings'}
                </div>
              )}
            </div>
          )}

          {/* LEVEL 2: Findings for selected resource */}
          {selectedResource && (
            <div className="space-y-1.5">
              {filteredFindings.map((f, idx) => {
                const c = SEV[f.severity];
                const sel = selectedFinding?.id === f.id;
                const rem = showRem === f.id;
                
                return (
                  <div 
                    key={`${f.id}-${idx}`} 
                    className={`rounded-lg border transition-all ${
                      sel 
                        ? `${c.semanticSelected} shadow-sm` 
                        : 'border-semantic-border-primary bg-semantic-bg-secondary hover:border-semantic-border-secondary'
                    }`}
                  >
                    {/* Finding Header */}
                    <div 
                      className="px-3 py-2 cursor-pointer" 
                      onClick={() => onSelectFinding(sel ? null : f)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${c.bg} text-white shrink-0`}>
                            {f.severity.slice(0,4).toUpperCase()}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-semantic-text-primary text-xs truncate">{f.title}</div>
                            <div className="text-[10px] text-semantic-text-secondary flex items-center gap-1.5">
                              <span className="text-semantic-text-brand">{f.frameworks[0]}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 shrink-0">
                          <button 
                            onClick={e => { e.stopPropagation(); onAskAI(f); }} 
                            className="p-1.5 hover:bg-semantic-bg-brand-secondary rounded text-semantic-text-secondary hover:text-semantic-text-brand transition-colors" 
                            title="Ask AI for help"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Finding Details (when selected) */}
                    {sel && (
                      <div className="px-4 pb-3 border-t border-semantic-border-secondary pt-3">
                        <p className="text-sm text-semantic-text-secondary mb-3">{f.description}</p>
                        
                        {/* Violating Rule */}
                        <div className="bg-semantic-bg-tertiary rounded-lg p-3 mb-3">
                          <div className="text-xs font-semibold text-semantic-text-secondary uppercase mb-2">Violating Rule</div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-semantic-text-secondary">Direction:</span>{' '}
                              <span className="font-medium text-semantic-text-primary">{f.violatingRule.direction}</span>
                            </div>
                            <div>
                              <span className="text-semantic-text-secondary">Protocol:</span>{' '}
                              <span className="font-medium text-semantic-text-primary">{f.violatingRule.protocol}</span>
                            </div>
                            <div>
                              <span className="text-semantic-text-secondary">Port:</span>{' '}
                              <span className="font-mono font-medium text-semantic-text-primary">{f.violatingRule.portRange}</span>
                            </div>
                            <div>
                              <span className="text-semantic-text-secondary">Source:</span>{' '}
                              <span className="font-mono font-medium text-semantic-text-danger">{f.violatingRule.source}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Remediation Toggle */}
                        <button 
                          onClick={() => setShowRem(rem ? null : f.id)} 
                          className="w-full text-left px-3 py-2 bg-semantic-bg-success-secondary hover:bg-semantic-bg-success-primary rounded-lg flex items-center justify-between transition-colors"
                        >
                          <span className="text-sm font-medium text-semantic-text-success">Remediation</span>
                          {rem ? <ChevronUp className="w-4 h-4 text-semantic-text-success" /> : <ChevronDown className="w-4 h-4 text-semantic-text-success" />}
                        </button>
                        
                        {/* Remediation Details */}
                        {rem && (
                          <div className="mt-3 space-y-3">
                            <p className="text-sm text-semantic-text-secondary">{f.remediation.description}</p>
                            
                            {f.remediation.awsCli && (
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <Terminal className="w-4 h-4 text-semantic-text-secondary" />
                                  <span className="text-xs font-semibold text-semantic-text-secondary">AWS CLI</span>
                                </div>
                                <pre className="bg-semantic-bg-tertiary text-semantic-text-success text-xs p-3 rounded-lg overflow-x-auto border border-semantic-border-primary">
                                  {f.remediation.awsCli}
                                </pre>
                              </div>
                            )}
                            
                            {f.remediation.ociCli && (
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <Terminal className="w-4 h-4 text-semantic-text-secondary" />
                                  <span className="text-xs font-semibold text-semantic-text-secondary">OCI CLI</span>
                                </div>
                                <pre className="bg-semantic-bg-tertiary text-semantic-text-success text-xs p-3 rounded-lg overflow-x-auto border border-semantic-border-primary">
                                  {f.remediation.ociCli}
                                </pre>
                              </div>
                            )}
                            
                            {f.remediation.terraform && (
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <FileCode className="w-4 h-4 text-semantic-text-secondary" />
                                  <span className="text-xs font-semibold text-semantic-text-secondary">Terraform</span>
                                </div>
                                <pre className="bg-semantic-bg-tertiary text-semantic-text-brand text-xs p-3 rounded-lg overflow-x-auto max-h-32 border border-semantic-border-primary">
                                  {f.remediation.terraform}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              
              {filteredFindings.length === 0 && (
                <div className="text-center py-8 text-semantic-text-secondary">
                  {filter ? `No ${filter} severity findings for this resource` : 'No findings'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
